import React, { useMemo } from 'react';
import { TrendingUp, List, Grid, X, Heart, ShieldCheck } from 'lucide-react';
import { SourceIcon } from '@/components/ui';
import { convertToProxyUrl } from '@/services/proxy';
import type { SearchResult, FavoriteItem, Category, MajorCategory } from '@/types';

interface SearchResultItem extends SearchResult {
  subtitle?: string;
  siteType?: string;
  category?: string;
}

interface SearchResultsPanelProps {
  results: SearchResultItem[];
  viewMode: 'grid' | 'list';
  isAuthenticated: boolean;
  isProxyEnabled: boolean;
  favorites: FavoriteItem[];
  categories: Category[];
  majorCategories: MajorCategory[];
  onViewModeChange: (mode: 'grid' | 'list') => void;
  onClose: () => void;
  onToggleFavorite: (result: SearchResultItem) => void;
}

const getIconBg = (siteType?: string, customColor?: string | null): string => {
  if (customColor) return '';
  switch (siteType) {
    case 'browse': return 'bg-[#F8F0FF] dark:bg-purple-900/20';
    case 'reference': return 'bg-[#FFF7ED] dark:bg-amber-900/20';
    default: return 'bg-[#F0F7FF] dark:bg-blue-900/20';
  }
};

export const SearchResultsPanel: React.FC<SearchResultsPanelProps> = ({
  results,
  viewMode,
  isAuthenticated,
  isProxyEnabled,
  favorites,
  categories,
  majorCategories,
  onViewModeChange,
  onClose,
  onToggleFavorite,
}) => {
  const isFavorite = (url: string) => favorites.some(f => f.url === url);

  const getCategoryInfo = (categoryId?: string) => {
    const category = categories.find(c => c.id === categoryId);
    const majorCategory = category ? majorCategories.find(mc => mc.id === category.majorCategoryId) : null;
    return {
      label: category?.name || '搜索',
      majorColor: majorCategory?.color || null,
    };
  };

  const getCategoryBadge = (categoryId?: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return 'bg-[#F0F7FF] text-[#3A7CFD] dark:bg-blue-900/30 dark:text-blue-400';
    const majorCategory = majorCategories.find(mc => mc.id === category.majorCategoryId);
    if (majorCategory?.color) return '';
    return 'bg-[#F0F7FF] text-[#3A7CFD] dark:bg-blue-900/30 dark:text-blue-400';
  };

  const getCategoryLabel = (categoryId?: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '搜索';
  };

  const getCategoryStyle = (categoryId?: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return {};
    const majorCategory = majorCategories.find(mc => mc.id === category.majorCategoryId);
    if (majorCategory?.color) return { backgroundColor: `${majorCategory.color}20`, color: majorCategory.color };
    return {};
  };

  const getAccentStyle = (categoryId?: string): React.CSSProperties => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return {};
    const majorCategory = majorCategories.find(mc => mc.id === category.majorCategoryId);
    if (majorCategory?.color) {
      return {
        '--card-accent': majorCategory.color,
        '--card-accent-20': `${majorCategory.color}33`,
      } as React.CSSProperties;
    }
    return {};
  };

  const groupedResults = useMemo(() => {
    if (viewMode !== 'grid') return null;
    const groups = new Map<string, SearchResultItem[]>();
    results.forEach(r => {
      const key = r.category || 'default';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [results, viewMode]);

  if (results.length === 0) return null;

  const renderResultCard = (result: SearchResultItem, index: number) => {
    const catInfo = getCategoryInfo(result.category);
    const accentStyle = getAccentStyle(result.category);
    const fav = isFavorite(result.url || '');
    const delay = Math.min(index * 50, 300);
    const iconBg = catInfo.majorColor
      ? { backgroundColor: `${catInfo.majorColor}18` }
      : undefined;

    if (viewMode === 'grid') {
      return (
        <div
          key={index}
          className="group result-card flex flex-col"
          style={{ animationDelay: `${delay}ms`, ...accentStyle }}
        >
          <div
            className="h-1.5 w-full shrink-0 rounded-t-2xl opacity-60"
            style={{ backgroundColor: catInfo.majorColor || undefined }}
          />

          <div className="flex flex-col flex-1 p-4">
            {/* Icon + Title (top-aligned, 12px gap) */}
            <div className="flex items-start gap-3 mb-1.5">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${getIconBg(result.siteType, catInfo.majorColor)}`}
                style={iconBg}
              >
                <SourceIcon icon={result.sourceIcon} name={result.sourceName} size="sm" />
              </div>
              <div className="min-w-0 flex-1">
                {result.url ? (
                  <a
                    href={isProxyEnabled ? convertToProxyUrl(result.url) : result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#1A1A1A] dark:text-gray-100 text-sm leading-tight line-clamp-2 hover:text-[#346FE8] dark:hover:text-blue-400 transition-colors"
                  >
                    {result.sourceName}
                  </a>
                ) : (
                  <h3 className="font-semibold text-[#1A1A1A] dark:text-gray-100 text-sm leading-tight line-clamp-2">
                    {result.sourceName}
                  </h3>
                )}
              </div>
            </div>

            {/* Description (6px below title, 14px above footer) */}
            {result.subtitle && (
              <p className="text-[#666666] dark:text-gray-400 text-xs leading-relaxed line-clamp-3 mb-0 flex-1">
                {result.subtitle}
              </p>
            )}
            {!result.subtitle && <div className="flex-1" />}

            {/* Footer: tag + actions */}
            <div className="flex items-center justify-between gap-2 pt-[14px] mt-0 border-t border-surface-100 dark:border-surface-800">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className={`text-[11px] px-2 py-0.5 rounded font-medium ${getCategoryBadge(result.category)}`}
                  style={getCategoryStyle(result.category)}
                >
                  {getCategoryLabel(result.category)}
                </span>
                {isProxyEnabled && (
                  <span className="flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded font-medium bg-[#F0FDF4] text-[#16A34A] dark:bg-green-900/30 dark:text-green-400">
                    <ShieldCheck className="w-2.5 h-2.5" />
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isAuthenticated && (
                  <button
                    onClick={() => onToggleFavorite(result)}
                    className={`p-1.5 rounded-lg transition-all ${
                      fav
                        ? 'text-[#DC2626] bg-[#FEF2F2] dark:bg-red-900/20'
                        : 'text-surface-400 hover:text-[#DC2626] hover:bg-[#FEF2F2] dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100'
                    }`}
                    title={fav ? '取消收藏' : '收藏'}
                  >
                    <Heart className={`w-3.5 h-3.5 ${fav ? 'fill-current' : ''}`} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // === LIST VIEW ===
    return (
      <div
        key={index}
        className="group result-card !rounded-xl flex items-stretch gap-0 overflow-hidden"
        style={{ animationDelay: `${delay}ms`, ...accentStyle }}
      >
        <div
          className="w-1 shrink-0"
          style={{ backgroundColor: catInfo.majorColor || 'transparent' }}
        />

        <div className="flex items-start gap-3 px-4 py-3 flex-1 min-w-0">
          {/* 48px icon container */}
          <div
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shrink-0 ${getIconBg(result.siteType, catInfo.majorColor)}`}
            style={iconBg}
          >
            <SourceIcon icon={result.sourceIcon} name={result.sourceName} size="md" />
          </div>

          {/* Content: title + desc */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {result.url ? (
                <a
                  href={isProxyEnabled ? convertToProxyUrl(result.url) : result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#1A1A1A] dark:text-gray-100 text-sm sm:text-base leading-tight hover:text-[#346FE8] dark:hover:text-blue-400 transition-colors"
                >
                  {result.sourceName}
                </a>
              ) : (
                <h3 className="font-semibold text-[#1A1A1A] dark:text-gray-100 text-sm sm:text-base leading-tight">
                  {result.sourceName}
                </h3>
              )}
              <span
                className={`text-[11px] px-2 py-0.5 rounded font-medium shrink-0 ${getCategoryBadge(result.category)}`}
                style={getCategoryStyle(result.category)}
              >
                {getCategoryLabel(result.category)}
              </span>
              {isProxyEnabled && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-medium bg-[#F0FDF4] text-[#16A34A] dark:bg-green-900/30 dark:text-green-400">
                  <ShieldCheck className="w-2.5 h-2.5" />代理
                </span>
              )}
            </div>
            {result.subtitle && (
              <p className="text-sm text-[#666666] dark:text-gray-400 mt-1.5 line-clamp-1 leading-relaxed">
                {result.subtitle}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {isAuthenticated && (
              <button
                onClick={() => onToggleFavorite(result)}
                className={`p-2 rounded-lg transition-all ${
                  fav
                    ? 'text-[#DC2626] bg-[#FEF2F2] dark:bg-red-900/20'
                    : 'text-surface-400 hover:text-[#DC2626] hover:bg-[#FEF2F2] dark:hover:bg-red-900/20'
                }`}
                title={fav ? '取消收藏' : '收藏'}
              >
                <Heart className={`w-4 h-4 sm:w-[18px] sm:h-[18px] ${fav ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-surface-900/80 rounded-2xl shadow-xl shadow-surface-900/5 border border-surface-200/60 dark:border-surface-700/60 p-3 sm:p-5 mb-4 sm:mb-6 backdrop-blur-sm animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <div className="flex items-center gap-2 sm:gap-2.5">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-md shadow-primary-500/20">
            <TrendingUp className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-white" />
          </div>
          <div>
            <span className="font-bold text-surface-900 dark:text-surface-100 text-sm sm:text-base">搜索结果</span>
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-[#F0F7FF] text-[#3A7CFD] dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
              {results.length} 条
            </span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'list'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
            }`}
            title="列表视图"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-2 rounded-lg transition-all ${
              viewMode === 'grid'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800'
            }`}
            title="网格视图"
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-surface-400 hover:text-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 transition-all ml-1"
            title="关闭结果"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Results */}
      {viewMode === 'list' ? (
        <div className="space-y-2">
          {results.map((result, index) => renderResultCard(result, index))}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedResults!.map(([categoryId, items]) => {
            const catInfo = getCategoryInfo(categoryId === 'default' ? undefined : categoryId);
            return (
              <div key={categoryId}>
                {groupedResults!.length > 1 && (
                  <div className="flex items-center gap-2 mb-3">
                    <h4
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: catInfo.majorColor || undefined }}
                    >
                      {catInfo.label}
                    </h4>
                    <div className="flex-1 h-px bg-surface-200 dark:bg-surface-700/50" />
                    <span className="text-[11px] text-surface-400 font-medium">{items.length}</span>
                  </div>
                )}
                <div className="grid gap-3 sm:gap-4 grid-cols-4">
                  {items.map((result) => renderResultCard(result, results.indexOf(result)))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stagger animation */}
      <style>{`
        @keyframes result-fade-in {
          from {
            opacity: 0;
            transform: translateY(12px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .result-card {
          animation: result-fade-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};
